import { Gavel, ShieldCheck, KeyRound, Clock, Quote } from "lucide-react";

// Plain-English explainer for USDOT # and MC operating authority (guide Step/Mission 3).
export default function AuthorityExplainer() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8" data-testid="authority-explainer">
      <div className="flex items-center gap-2 mb-3">
        <Gavel className="w-4 h-4 text-brand" />
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Plain-English explainer</span>
      </div>
      <h3 className="font-heading text-2xl font-black tracking-tight text-slate-900">USDOT # vs. MC Authority</h3>
      <p className="mt-3 text-slate-700 leading-relaxed">
        These two get mixed up constantly. Simple version: your <strong>USDOT number</strong> is <em>who you are</em>
        {" "}(your safety identity), and your <strong>MC number</strong> is <em>what you're allowed to do</em> (haul
        for-hire freight across state lines). Most for-hire interstate owner-operators need <strong>both</strong>.
      </p>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">USDOT number</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            A unique ID from the FMCSA that tracks your safety record — inspections, crashes, audits and your CSA score.
            It's your business's <strong>safety identity</strong> and follows you everywhere.
          </p>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <KeyRound className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">MC number (authority)</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            "MC" = Motor Carrier. It's your federal <strong>permission to haul regulated freight for money</strong>
            {" "}across state lines. Private carriers (hauling only your own goods) or purely intrastate operations may
            not need one — but for-hire interstate does.
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-3 rounded-xl bg-white border-l-4 border-amber-500 p-5">
        <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900 leading-relaxed">
          <strong>The 21-day protest period:</strong> once you file for MC authority, FMCSA opens a 21-day window before
          it activates. Hauling a regulated for-hire load during that window is operating illegally — brokers won't pay
          you and you can lose the authority you just paid for. Each authority costs <strong>$300</strong>.
        </p>
      </div>

      <div className="mt-5 rounded-xl bg-brand/5 border border-brand/20 p-5">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-7 h-7 rounded-full bg-brand flex items-center justify-center shrink-0">
            <Quote className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">Mentor tip</span>
        </div>
        <p className="text-sm text-slate-800 leading-relaxed">
          Write down both numbers the moment they're issued — you'll type them on insurance, factoring, broker and
          load-board setups constantly. Track your application on FMCSA's Licensing &amp; Insurance system, and don't
          book your first load until your authority reads <strong>active</strong>.
        </p>
      </div>
    </div>
  );
}
